import React, { useLayoutEffect, useRef, useState } from 'react';
import { Panel, Waterfall, BarList, Empty, Delta, StatusIcon, STATUS, Hint, KpiRow, channelColor, plural, fmtRub, fmtPct, fmtNum } from './charts';
import ExpensesMonth from './ExpensesMonth';

// Свои каналы без пометки: деньги из заказа. Площадка — «факт» (прислала цифры) или «оценка»
const MONEY_CHIP = {
  actual: { label: 'факт', cls: 'an-chip-fact' },
  estimate: { label: 'оценка', cls: 'an-chip-est' },
};

/** Ячейка с числом и значком статуса по порогу (значок + подпись для экранного чтения) */
function FlagCell({ status, text, children, className = 'r' }) {
  const st = status ? STATUS[status] : null;
  return (
    <td className={`${className}${st ? ` an-cell ${st.cls}` : ''}`} title={text || undefined}>
      {st && <span className="an-flag" role="img" aria-label={`${st.word}: ${text}`}><StatusIcon status={status} size={12} /></span>}
      {children}
    </td>
  );
}

/** Деньги площадки — раскрываются под строкой её канала */
function PlatformMoney({ pf }) {
  const [showAll, setShowAll] = useState(false);
  const mm = showAll ? pf.mismatch : pf.mismatch.slice(0, 5);
  return (
    <div className="an-pf">
      <div className="an-subtitle" style={{ marginTop: 0 }}>Деньги площадки: {pf.title} · {plural(pf.orders, 'заказ', 'заказа', 'заказов')}</div>
      <div className="an-mini-tiles">
        <div className="an-mini"><span className="an-muted">Оборот на площадке</span><b>{fmtRub(pf.turnover)}</b></div>
        <div className="an-mini an-mini-accent"><span className="an-muted">Удержала площадка</span><b>{fmtRub(pf.held)}</b>
          <span className="an-small">{pf.held_pct != null ? `${fmtPct(pf.held_pct)} оборота` : ''}</span></div>
        <div className="an-mini an-mini-ok"><span className="an-muted">Осталось магазину</span><b>{fmtRub(pf.payout)}</b>
          {pf.estimate_payout ? <span className="an-small">из них оценка {fmtRub(pf.estimate_payout)}</span> : null}</div>
        <div className="an-mini"><span className="an-muted">По прямой ссылке</span><b>{plural(pf.direct_orders, 'заказ', 'заказа', 'заказов')}</b>
          <span className="an-small">{pf.direct_orders ? `${fmtRub(pf.direct_turnover)}, удержано ${fmtRub(pf.direct_held)}` : 'без комиссии площадки'}</span></div>
      </div>
      {pf.chain && (
        <div className="an-chain">
          <div className="an-subtitle" style={{ marginTop: 14 }}>
            От наших цен к выплате <Hint text={'Те же заказы площадки (с её цифрами, без прямой ссылки): сначала по нашим ценам — стебли, сервисный сбор, доставка; дальше что добавила наценка на площадке и что площадка удержала. Итог — сколько пришло сверх или меньше наших цен. Около нуля — ставка в настройках подобрана верно.'} />
          </div>
          {pf.chain.rows.map(r => (
            <div key={r.key} className={`an-chain-row${r.kind === 'total' ? ' an-b' : ''}`}>
              <span>{r.kind === 'delta' ? (r.value >= 0 ? '+ ' : '− ') : r.key === 'payout' ? '= ' : ''}{r.label}</span>
              <span className="an-nowrap">{r.kind === 'delta' ? fmtRub(Math.abs(r.value)) : fmtRub(r.value)}</span>
            </div>
          ))}
          <div className={`an-chain-diff${pf.chain.diff < 0 ? ' an-bad' : ''}`}>
            Разница с нашими ценами: <b>{pf.chain.diff > 0 ? '+' : pf.chain.diff < 0 ? '−' : ''}{fmtRub(Math.abs(pf.chain.diff))}</b>
            {pf.chain.diff_pct != null && <> ({pf.chain.diff_pct > 0 ? '+' : ''}{fmtNum(pf.chain.diff_pct, 1)} %)</>}
            <span className="an-muted an-small"> · {plural(pf.chain.orders, 'заказ', 'заказа', 'заказов')}</span>
          </div>
          {pf.direct_orders > 0 && (
            <div className="an-muted an-small">
              Отдельно по прямой ссылке: {plural(pf.direct_orders, 'заказ', 'заказа', 'заказов')}, к выплате {pf.direct_vs_ours >= 0 ? '+' : '−'}{fmtRub(Math.abs(pf.direct_vs_ours))} к нашим ценам — комиссии нет, а наценка осталась
            </div>
          )}
        </div>
      )}
      <div className="an-text" style={{ marginTop: 12 }}>
        {pf.avg_rate != null && (
          <p>Удержание от цены товаров — в среднем <b>{fmtPct(pf.avg_rate)}</b>{pf.chain && pf.chain.fee_pct != null ? <> (комиссия {fmtPct(pf.chain.fee_pct)}, с бонусами покупателям {fmtPct(pf.chain.held_pct)})</> : null}; цены на площадке считаются под <b>{fmtPct(pf.setting_rate)}</b>.
            {pf.avg_rate != null && pf.setting_rate != null && pf.setting_rate - pf.avg_rate > 2 && ' Цены на площадке можно немного снизить: ставка в настройках выше фактического удержания.'}</p>
        )}
        {pf.estimated > 0 && <p className="an-muted">По {plural(pf.estimated, 'заказу', 'заказам', 'заказам')} площадка не прислала цифры — удержание посчитано по ставке недавних заказов (оценка).</p>}
      </div>
      {pf.delivery_unpaid.length > 0 && (
        <div className="an-muted an-small" style={{ marginTop: 10 }}>
          Доставка без адреса — доплату Flowwow в данных не показывает, в деньгах взята по нашему тарифу (оценка):{' '}
          {pf.delivery_unpaid.map(o => `#${o.number}`).join(', ')}
        </div>
      )}
      {pf.mismatch.length > 0 && (
        <>
          <div className="an-subtitle">Удержание выше настройки</div>
          <div className="an-scroll-x">
            <table className="an-table">
              <thead><tr><th>Заказ</th><th className="r">Товары</th><th className="r">Удержано, %</th><th className="r">Удержано</th><th className="r">Осталось</th></tr></thead>
              <tbody>
                {mm.map(m => (
                  <tr key={m.number}>
                    <td>#{m.number}</td><td className="r">{fmtRub(m.goods)}</td>
                    <FlagCell status={m.status} text={`удержано ${fmtPct(m.rate, 0)} от цены товаров при настройке ${fmtPct(pf.setting_rate, 0)}`}>{fmtPct(m.rate, 0)}</FlagCell><td className="r">{fmtRub(m.held)}</td><td className="r">{fmtRub(m.payout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pf.mismatch.length > 5 && (
            <button type="button" className="an-link" onClick={() => setShowAll(s => !s)}>{showAll ? 'Свернуть' : `Показать все (${pf.mismatch.length})`}</button>
          )}
        </>
      )}
    </div>
  );
}

/** Подробности канала: деньги по шагам; у площадки — её удержания и сверка */
function ChannelDetail({ r, pf }) {
  // У площадки свои плитки (оборот, удержала, осталось, прямая ссылка) — общие не повторяем (13.10)
  if (pf) return <div className="an-ch-detail"><PlatformMoney pf={pf} /></div>;
  return (
    <div className="an-ch-detail">
      <div className="an-mini-tiles">
        <div className="an-mini"><span className="an-muted">Средний чек</span><b>{fmtRub(r.avg)}</b></div>
        <div className="an-mini"><span className="an-muted">Удержано</span><b>{r.held ? `−${fmtRub(r.held)}` : '—'}</b>
          <span className="an-small">{r.held_pct ? `${fmtPct(r.held_pct)} оборота` : 'комиссий нет'}</span></div>
        <div className="an-mini"><span className="an-muted">Осталось магазину</span><b>{fmtRub(r.payout)}</b>
          <span className="an-small">{fmtNum(r.payout_share, 1)} % денег магазина</span></div>
        <div className="an-mini"><span className="an-muted">Валовая маржа</span><b>{fmtPct(r.margin_pct, 0)}</b>
          {MONEY_CHIP[r.money] && <span className="an-small">деньги: {MONEY_CHIP[r.money].label}</span>}</div>
      </div>
      <div className="an-ch-wf"><Waterfall rows={r.waterfall} /></div>
    </div>
  );
}

/** Затраты за период по статьям (из «Финансы → Затраты»): доля по дням, «план» — месяц не подтверждён */
function CostsPanel({ costs, onView }) {
  const planNote = (amount, plan) => (plan > 0
    ? <span className="an-muted an-small"> {plan >= amount - 1 ? 'план' : `из них по плану — ${fmtRub(plan)}`}</span>
    : null);
  return (
    <Panel title="Затраты за период" aside={onView && <button type="button" className="btn btn-secondary btn-sm" onClick={() => onView('expenses')}>Внести затраты</button>}>
      {!costs.any ? (
        <div className="an-muted">Аренда, зарплата, связь и другие затраты — внесите их, и появится операционная прибыль: сколько остаётся после всех затрат.</div>
      ) : (<>
      <div className="an-ex-costs">
        {(costs.by_category || []).map(c => (
          <div key={c.key} className="an-ex-cost-row">
            <span>{c.title}</span>
            <span className="an-nowrap">{fmtRub(c.amount)}{planNote(c.amount, c.plan)}</span>
          </div>
        ))}
        <div className="an-ex-cost-row an-b">
          <span>Всего</span>
          <span className="an-nowrap">{fmtRub(costs.total)}{planNote(costs.total, costs.plan)}</span>
        </div>
      </div>
      <div className="an-muted an-small" style={{ marginTop: 8 }}>Доля по дням выбранного периода. «План» — месяц ещё не подтверждён в «Затратах».</div>
      </>)}
    </Panel>
  );
}

/** «Прибыль по месяцам» — квартал, год, свой период от 2 месяцев (владелец 13.10): от оборота до
 *  операционной прибыли по столбцам-месяцам. «*» — в сумме есть план (месяц не подтверждён в «Затратах») */
function PnlPanel({ pnl, onView }) {
  const money = (v) => (v < 0 ? `−${fmtRub(-v)}` : fmtRub(v));
  const star = (row, i) => {
    const pl = i == null ? row.plan_total : row.plan?.[i];
    return pl > 0 ? <span className="an-muted" title={`из них по плану — ${fmtRub(pl)}`}>*</span> : null;
  };
  const anyPlan = pnl.rows.some(r => r.plan_total > 0);
  return (
    <Panel title="Прибыль по месяцам" aside={onView && <button type="button" className="an-link" onClick={() => onView('expenses')}>Затраты →</button>}>
      <div className="an-scroll-x">
        <table className="an-table an-pnl">
          <thead><tr>
            <th />
            {pnl.months.map((m, i) => <th key={m.key} className={`r${i < pnl.months.length - 1 ? ' an-m-hide' : ''}`}>{m.label}{m.days && <div className="an-muted an-small">{m.days}</div>}</th>)}
            <th className="r">Итого</th>
          </tr></thead>
          <tbody>
            {pnl.rows.map(r => (
              <tr key={r.key} className={`an-pnl-${r.kind}`}>
                <td>{r.label}</td>
                {r.values.map((v, i) => (
                  <td key={i} className={`r${r.kind === 'total' && v < 0 ? ' an-bad' : ''}${i < r.values.length - 1 ? ' an-m-hide' : ''}`}>{v ? money(v) : '—'}{star(r, i)}</td>
                ))}
                <td className={`r an-b${r.kind === 'total' && r.total < 0 ? ' an-bad' : ''}`}>{r.total ? money(r.total) : '—'}{star(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="an-muted an-small" style={{ marginTop: 8 }}>
        Заказы — по выбранному дню заказа, затраты — доля по дням месяца.{anyPlan ? ' * — в сумме есть план: месяц ещё не подтверждён в «Затратах».' : ''}
        {pnl.months.some(m => m.days) ? ' Неполные месяцы подписаны днями.' : ''}
        {pnl.rows.some(r => r.partial_total) ? ' Операционная прибыль — только за месяцы, где внесены затраты.' : ''}
        <span className="an-mobile-only"> На телефоне — последний месяц и итог.</span>
      </div>
    </Panel>
  );
}

/** «Финансы»: показатели, от оборота к прибыли, отмены, каналы продаж (раскладка 12.10);
 *  переключатель «Отчёт | Затраты» — затраты месяца с планом и фактом (13.10) */
export default function ChannelsTab({ data, blocks, compare, view = 'report', onView, showToast, onGoMarketing }) {
  const has = (b) => blocks.includes(b);
  const rows = data.rows || [];
  // Все каналы «раньше не было» — одной строкой под таблицей, а не у каждого (аудит 14.10)
  const noPrev = compare && rows.length > 0 && rows.every(r => r.delta == null);
  const [open, setOpen] = useState(null);
  const [allCancels, setAllCancels] = useState(false);   // причин отмен бывает ~70 — сначала первые 10 (UX 25.10)
  const pfFor = (key) => (has('platform_money') ? (data.platforms || []).find(pf => pf.name === key) : null);
  // Строка, по которой нажали, остаётся на том же месте экрана: без этого при сворачивании
  // длинных подробностей страница «прыгала» (13.10)
  const anchor = useRef(null);
  const toggle = (key, e) => {
    const row = e?.currentTarget;
    anchor.current = row ? { row, top: row.getBoundingClientRect().top } : null;
    setOpen(o => (o === key ? null : key));
  };
  useLayoutEffect(() => {
    const a = anchor.current;
    if (!a || !a.row.isConnected) return;
    const dy = a.row.getBoundingClientRect().top - a.top;
    if (Math.abs(dy) > 1) window.scrollBy(0, dy);
    anchor.current = null;
  }, [open]);
  // Затраты вносятся из панели «Затраты за период» (переключателя «Отчёт | Затраты» нет — владелец 14.10)
  if (onView && view === 'expenses') {
    return (
      <div className="an-stack">
        <div><button type="button" className="an-link" onClick={() => onView('report')}>← К отчёту</button></div>
        <ExpensesMonth showToast={showToast} onGoMarketing={onGoMarketing} />
      </div>
    );
  }
  return (
    <div className="an-stack">
      {has('finance_tiles') && <KpiRow tiles={data.finance_tiles || data.tiles} compare={compare} />}

      <div className="an-grid2">
        {has('money_waterfall') && (
          <Panel title="От оборота к прибыли">
            {data.totals.turnover ? <Waterfall rows={data.waterfall} /> : <Empty />}
          </Panel>
        )}
        <div className="an-stack">
          {onView && <CostsPanel costs={data.costs || {}} onView={onView} />}
        {has('cancel_reasons') && (
          <Panel title="Отмены" aside={<span className="an-muted">{data.cancels.count ? `${plural(data.cancels.count, 'отмена', 'отмены', 'отмен')} на ${fmtRub(data.cancels.sum)}` : ''}</span>}>
            {data.cancels.items.length > 1 ? (
              <>
                <BarList items={(allCancels ? data.cancels.items : data.cancels.items.slice(0, 10))
                  .map(c => ({ label: c.title, value: c.count, text: `${c.count} · ${fmtRub(c.sum)}` }))} />
                {data.cancels.items.length > 10 && (
                  <button type="button" className="an-link" style={{ marginTop: 6 }} onClick={() => setAllCancels(v => !v)}>
                    {allCancels ? 'Свернуть' : `Ещё ${plural(data.cancels.items.length - 10, 'причина', 'причины', 'причин')}`}
                  </button>
                )}
              </>
            ) : data.cancels.items.length === 1 ? (
              <div className="an-text"><p>{plural(data.cancels.items[0].count, 'отмена', 'отмены', 'отмен')} — «{data.cancels.items[0].title}», на {fmtRub(data.cancels.items[0].sum)}.</p></div>
            ) : <Empty>Отмен оплаченных заказов не было</Empty>}
            {data.cancels.unpaid_share != null && (
              <div className="an-muted an-small" style={{ marginTop: 8 }}>Ещё {fmtPct(data.cancels.unpaid_share)} оформленных заказов так и не оплатили — они в отмены не входят.</div>
            )}
            {data.cancels.count > 0 && <div className="an-muted an-small" style={{ marginTop: 4 }}>Причина — комментарий сотрудника при отмене.</div>}
          </Panel>
        )}
        </div>
      </div>

      {data.pnl && <PnlPanel pnl={data.pnl} onView={onView} />}

      {has('channel_table') && (
        <Panel title="Каналы продаж" aside={<span className="an-muted">Нажмите на канал — подробности</span>}>
          {rows.length ? (
            <div className="an-scroll-x">
              <table className="an-table an-table-click an-ch-table">
                <thead><tr>
                  <th>Канал</th><th className="r">Заказы</th><th className="r an-m-hide">Оборот</th><th className="r"><span className="an-desktop-only">Валовая прибыль</span><span className="an-mobile-only">Прибыль</span></th>
                </tr></thead>
                <tbody>
                  {rows.map((r, j) => (
                    <React.Fragment key={r.key}>
                      <tr className={open === r.key ? 'sel' : ''} onClick={(e) => toggle(r.key, e)} tabIndex={0} aria-expanded={open === r.key}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(r.key, e); } }}>
                        <td><span className="an-caret" aria-hidden="true">{open === r.key ? '▾' : '▸'}</span>
                          <span className="an-dot" style={{ background: channelColor(r.key, j) }} /> <b>{r.title}</b>
                          {compare && !noPrev && (r.delta != null
                            ? <span className="an-ch-delta"><Delta value={r.delta} /></span>
                            : <span className="an-ch-delta an-muted an-small" title="В прошлом периоде заказов из этого канала не было — сравнивать не с чем">раньше не было</span>)}</td>
                        <td className="r">{fmtNum(r.orders)}</td>
                        <td className="r an-m-hide">{fmtRub(r.turnover)}</td>
                        <FlagCell status={r.margin_status} text={r.margin_status_text}>
                          <b>{fmtRub(r.gross)}</b>{r.margin_pct != null ? <div className="an-muted an-small">маржа {fmtPct(r.margin_pct, 0)}</div> : null}
                        </FlagCell>
                      </tr>
                      {open === r.key && (
                        <tr className="an-expand"><td colSpan={4}><ChannelDetail r={r} pf={pfFor(r.key)} /></td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td>Всего</td><td className="r">{fmtNum(data.totals.orders)}</td><td className="r">{fmtRub(data.totals.turnover)}</td>
                  <td className="r">{fmtRub(data.totals.gross)}</td>
                </tr></tfoot>
              </table>
            </div>
          ) : <Empty />}
          <div className="an-muted an-small" style={{ marginTop: 8 }}>
            {noPrev && 'В прошлом периоде заказов не было — сравнивать не с чем. '}Оборот площадки — в её ценах, с наценкой под комиссию. Сравнивать каналы честнее по валовой прибыли.
          </div>
        </Panel>
      )}
    </div>
  );
}
